import { Box } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { ReactElement } from 'react';
import { tokens } from '@synergy/theme-palo';

import { PoolProductAutocompleteList } from '../autocomplete-list/product-autocomplete-list/pool-product-autocomplete-list';
import { Product } from '../../models/product';

export interface PaloRowProductSearchProps {
    onSelected: (product: Product) => void;
    /** Restricts the search to the supplier the document is with. */
    supplierIds?: number[];
    includeNormArticles?: boolean;
    /** For the message when the supplier carries nothing that matches. */
    supplierName?: string;
    /**
     * The document is with one supplier, and none is chosen yet. Searching
     * would then show the articles of every supplier, and nothing says so —
     * the case the article of a wrong supplier came in through (Prüfbericht
     * 28.09.2026, T2/B1).
     */
    supplierMissing?: boolean;
}

/**
 * The article search inside an empty position row.
 *
 * A row added with "Zeile hinzufügen" arrives without an article, and it is
 * searched for in the row itself — the way ERPNext fills a new grid row,
 * instead of asking for the article in a dialog first (#2208).
 *
 * The marker class is what keeps the field visible. The table draws its fields
 * without an outline, because everything else in a row is a value that already
 * stands there; an empty row drawn that way is an empty cell nobody can see.
 */
export const PaloRowProductSearch = ({
    onSelected,
    supplierIds,
    includeNormArticles = true,
    supplierName,
    supplierMissing = false,
}: PaloRowProductSearchProps): ReactElement => {
    const intl = useIntl();

    if (supplierMissing) {
        return (
            <Box sx={{ fontSize: '12.5px', color: tokens.color.ink3, padding: '7px 0' }}>
                <FormattedMessage
                    id="app.positions.chooseSupplierFirst"
                    defaultMessage="Bitte zuerst Lieferanten wählen"
                />
            </Box>
        );
    }

    return (
        <Box className="palo-row-search">
            <PoolProductAutocompleteList
                onSelected={product => product && onSelected(product)}
                clearInputOnSelect={true}
                autoFocus={true}
                placeholder={intl.formatMessage({
                    id: 'app.positions.searchArticle',
                    defaultMessage: 'Artikel suchen …',
                })}
                label={<FormattedMessage id="app.label.product" />}
                supplierIds={supplierIds}
                includeNormArticles={includeNormArticles}
                emptyMessage={
                    supplierName ? (
                        <FormattedMessage
                            id="app.positions.noArticlesOfSupplier"
                            defaultMessage="Keine Artikel von {supplier} gefunden"
                            values={{ supplier: supplierName }}
                        />
                    ) : (
                        <FormattedMessage id="app.positions.noArticles" defaultMessage="Keine Artikel gefunden" />
                    )
                }
            />
        </Box>
    );
};
